import { forwardRef, useImperativeHandle, useState } from "react";
import { Dropdown, Icon } from "semantic-ui-react";

import type { CUSTOMER_TYPES } from "models";
import type { DATE_ORDER_BY, WO_FILTERS } from "modules/Appointments";
import type { AppointmentTableHeaderDropdownOption } from "modules/Appointments/components/AppointmentTable/hooks";
import type { ReactNode } from "react";

type ColumnDataSingleSelectorType<T extends string | WO_FILTERS | DATE_ORDER_BY | CUSTOMER_TYPES> = {
  options: AppointmentTableHeaderDropdownOption<T>[];
  trigger: ReactNode;
  selected: T;
  multiple?: false;
  setSelectedValue: (value: T) => void;
};

type ColumnDataMultipleStringSelectorType = {
  trigger: ReactNode;
  options: AppointmentTableHeaderDropdownOption<string>[];
  multiple: true;
  selected: string[];
  setSelectedValue: (value: string) => void;
};

type ColumnDataSelectorProps =
  | ColumnDataSingleSelectorType<string>
  | ColumnDataSingleSelectorType<WO_FILTERS>
  | ColumnDataSingleSelectorType<DATE_ORDER_BY>
  | ColumnDataSingleSelectorType<CUSTOMER_TYPES>
  | ColumnDataMultipleStringSelectorType;

const isString = (props: ColumnDataSelectorProps): props is ColumnDataSingleSelectorType<string> => {
  return typeof props.options[0].value === "string";
};

const isCustomerTypes = (props: ColumnDataSelectorProps): props is ColumnDataSingleSelectorType<CUSTOMER_TYPES> => {
  return typeof props.options[0].value !== "string";
};

export type ColumnDataSelectorChildMethods = {
  toggleDropdown: () => void;
};

export const ColumnDataSelector = forwardRef<ColumnDataSelectorChildMethods, ColumnDataSelectorProps>((props, ref) => {
  const { selected, multiple = false, options, trigger } = props;
  const [open, setOpen] = useState(false);

  const toggleFilterDropdown = () => {
    setOpen(p => !p);
  };

  useImperativeHandle(ref, () => ({
    toggleDropdown: toggleFilterDropdown
  }));

  return (
    <div className="appointment-table-flex divider" onClick={toggleFilterDropdown}>
      <Dropdown multiple={multiple} trigger={trigger} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} icon={false}>
        <Dropdown.Menu>
          {options.map(option => {
            return (
              <Dropdown.Item
                selected={Array.isArray(selected) && typeof option.value === "string" ? selected.includes(option.value) : selected === option.value}
                key={`filter-appointment-table-${option.key}`}
                onClick={() => {
                  setOpen(false);
                  if (isString(props) && typeof option.key === "string") props.setSelectedValue(option.key);
                  else if (isCustomerTypes(props) && typeof option.key !== "string") props.setSelectedValue(option.key);
                }}
              >
                <div className="last-user-dropdown-selection">
                  <p className="text">{option.text}</p>
                  {option.icon && <Icon className={option.icon} />}
                </div>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
});

ColumnDataSelector.displayName = "ColumnDataSelector";
